import { Check, ChevronRight } from "lucide-react";
import { useState } from "react";
import { WardrobeImg } from "@/components/WardrobeImg";
import { trackEvent } from "@/lib/analytics";
import { getSizeLabelForItem, type ActiveSize } from "@/lib/sizeLabels";
// Wave 3B — Commit 3d: offline-shopping badge on per-gap-item rows.
import OfflineShoppingBadge from "@/components/shopping/OfflineShoppingBadge";
import {
  hasOfflineShoppingData,
  type OutfitItemOfflineShopping,
} from "@/hooks/useOutfitItems";

const CORAL = "var(--l2f-coral)";


export type Lb3Outcome =
  | "outcome_1_full"
  | "outcome_2_partial"
  | "outcome_3_no_match"
  | "empty_wardrobe";

export interface Lb3OwnedItem {
  id: string | null;
  name: string | null;
  category: string | null;
  color_hex?: string | null;
  image_url?: string | null;
  image_path?: string | null;
}

export interface Lb3GapItem {
  category: string | null;
  name: string | null;
  color_hex?: string | null;
  tiers?: {
    mid?: {
      title?: string | null;
      thumbnail?: string | null;
      source?: string | null;
      link?: string | null;
      price?: string | null;
    } | null;
  } | null;
}

interface ShoppingListProps {
  outfitId: string;
  outcome: Lb3Outcome;
  ownedItems: Lb3OwnedItem[];
  gapItems: Lb3GapItem[];
  activeSizeProfile?: ActiveSize | null;
  onOpenWardrobeItem?: (id: string) => void;
  onEnlargeImage?: (src: string, alt: string, source: string) => void;
  // Wave 3B — Commit 3d: offline-shopping wiring.
  offlineShoppingActive?: boolean;
  getGapItemOfflineShopping?: (
    item: Lb3GapItem,
    index: number,
  ) => Partial<OutfitItemOfflineShopping> | null;
  onOpenOfflineStores?: (
    item: Lb3GapItem,
    offlineItem: Partial<OutfitItemOfflineShopping>,
  ) => void;
}

export function Lb3ShoppingList({
  outfitId,
  outcome,
  ownedItems,
  gapItems,
  activeSizeProfile = null,
  onOpenWardrobeItem,
  onEnlargeImage,
  offlineShoppingActive = false,
  getGapItemOfflineShopping,
  onOpenOfflineStores,
}: ShoppingListProps) {

  const showNoMatch = outcome === "outcome_3_no_match";
  return (
    <div className="lb3-shopping-list mt-4 flex flex-col gap-4">
      {showNoMatch && (
        <p
          className="text-xs"
          style={{ color: "var(--l2f-muted, hsl(var(--muted-foreground)))" }}
        >
          None of your wardrobe items matched this look.
        </p>
      )}

      {ownedItems.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            Your wardrobe
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Style it with what you already own
          </p>
          <ul className="mt-2 flex flex-col gap-2">
            {ownedItems.map((item) => (
              <OwnedItemRow
                key={item.id ?? item.name ?? Math.random().toString(36)}
                item={item}
                outfitId={outfitId}
                outcome={outcome}
                onOpen={onOpenWardrobeItem}
              />
            ))}
          </ul>
        </section>
      )}

      {gapItems.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            You might also want
          </h4>
          <ul className="mt-2 flex flex-col gap-2">
            {gapItems.map((item, i) => {
              const offlineItem =
                offlineShoppingActive && getGapItemOfflineShopping
                  ? getGapItemOfflineShopping(item, i) ?? null
                  : null;
              return (
                <GapItemRow
                  key={`${item.name ?? "gap"}-${i}`}
                  item={item}
                  outfitId={outfitId}
                  outcome={outcome}
                  activeSizeProfile={activeSizeProfile}
                  onEnlargeImage={onEnlargeImage}
                  offlineShoppingActive={offlineShoppingActive}
                  offlineShoppingItem={offlineItem}
                  onOpenOfflineStores={
                    offlineItem && onOpenOfflineStores
                      ? () => onOpenOfflineStores(item, offlineItem)
                      : undefined
                  }
                />
              );
            })}

          </ul>
        </section>
      )}
    </div>
  );
}

function OwnedItemRow({
  item,
  outfitId,
  outcome,
  onOpen,
}: {
  item: Lb3OwnedItem;
  outfitId: string;
  outcome: Lb3Outcome;
  onOpen?: (id: string) => void;
}) {
  const click = () => {
    if (!item.id) return;
    trackEvent("lb3_owned_item_clicked", {
      outfit_id: outfitId,
      wardrobe_item_id: item.id,
      outcome,
    });
    onOpen?.(item.id);
  };
  const [imgBroken, setImgBroken] = useState(false);
  const hasImageSource = !!(item.image_url || item.image_path);
  return (
    <li>
      <button
        type="button"
        onClick={click}
        className="w-full flex items-center gap-3 p-2 rounded-md text-left hover:bg-muted/40 transition-colors"
      >
        {hasImageSource && !imgBroken ? (
          <WardrobeImg
            item={{
              id: item.id ?? "",
              image_url: item.image_url ?? null,
              image_path: item.image_path ?? null,
              cutout_url: null,
              cutout_status: null,
            }}
            alt={item.name ?? ""}
            onError={() => setImgBroken(true)}
            className="w-12 h-12 rounded object-cover flex-shrink-0"
          />
        ) : (
          <div
            className="w-12 h-12 rounded flex-shrink-0"
            style={{ background: item.color_hex ?? "var(--secondary)" }}
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{item.name ?? "Item"}</p>
          <p className="text-xs text-muted-foreground">Your wardrobe</p>
        </div>
        <Check
          className="w-4 h-4 flex-shrink-0"
          style={{ color: "#16a34a" }}
          aria-label="Owned"
        />
      </button>
    </li>
  );
}

// TODO: extract shared ShopRow component used by main shopping list +
// styled-costs + lb3 gap rows. Currently duplicating to ship the visual fix.
function GapItemRow({
  item,
  outfitId,
  outcome,
  activeSizeProfile,
  onEnlargeImage,
  offlineShoppingActive = false,
  offlineShoppingItem = null,
  onOpenOfflineStores,
}: {
  item: Lb3GapItem;
  outfitId: string;
  outcome: Lb3Outcome;
  activeSizeProfile?: ActiveSize | null;
  onEnlargeImage?: (src: string, alt: string, source: string) => void;
  offlineShoppingActive?: boolean;
  offlineShoppingItem?: Partial<OutfitItemOfflineShopping> | null;
  onOpenOfflineStores?: () => void;
}) {

  // Wave 3B — Commit 3d: badge vs "Online only" fallback per gap item.
  const offlineHasStores = hasOfflineShoppingData(offlineShoppingItem);
  const offlineResolverRan =
    !!offlineShoppingItem?.offline_shopping_source &&
    offlineShoppingItem.offline_shopping_source !== "skipped" &&
    offlineShoppingItem.offline_shopping_source !== "no_location";
  const showOfflineBadge = offlineShoppingActive && offlineHasStores;
  const showOfflineFallback =
    offlineShoppingActive && !offlineHasStores && offlineResolverRan;

  const product = item.tiers?.mid ?? null;
  const link = product?.link ?? null;
  const retailer = product?.source ?? null;
  const price = product?.price ?? null;
  const thumb = product?.thumbnail ?? null;
  const title = product?.title ?? item.name ?? "Item";

  const onBuyClick = () => {
    trackEvent("lb3_gap_item_clicked", {
      outfit_id: outfitId,
      retailer,
      product_url: link,
      outcome,
    });
  };

  const onImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!thumb) return;
    trackEvent("item_image_enlarged", {
      outfit_id: outfitId,
      source: "lb3_gap_items",
    });
    onEnlargeImage?.(thumb, title, "lb3_gap_items");
  };

  const BuyContainer: React.ElementType = link ? "a" : "div";
  const buyProps = link
    ? {
        href: link,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: onBuyClick,
        "aria-label": `Buy ${title}${retailer ? ` from ${retailer}` : ""}`,
      }
    : {};

  return (
    <li>
      {/* Wave 3B — Commit 3d: offline-shopping surface per gap item */}
      {(showOfflineBadge || showOfflineFallback) && (
        <div style={{ marginBottom: 4, display: "flex", gap: 6 }}>
          {showOfflineBadge ? (
            <OfflineShoppingBadge
              item={offlineShoppingItem ?? undefined}
              onTap={onOpenOfflineStores}
              compact
            />
          ) : (
            <span
              style={{
                fontSize: 11,
                fontStyle: "italic",
                color: "#9A9490",
                lineHeight: 1,
              }}
            >
              Online only
            </span>
          )}
        </div>
      )}
      <div
        className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted/40 transition-colors"
      >
        {thumb ? (
          <button
            type="button"
            onClick={onImageClick}
            aria-label={`Enlarge ${title}`}
            className="flex-shrink-0 p-0 border-0 bg-transparent cursor-zoom-in"
          >
            <img
              src={thumb}
              alt={title}
              className="w-12 h-12 rounded object-cover opacity-90"
            />
          </button>
        ) : (
          <div
            className="w-12 h-12 rounded flex-shrink-0"
            style={{ background: item.color_hex ?? "var(--secondary)" }}
          />
        )}
        <BuyContainer
          {...buyProps}
          className="flex-1 min-w-0 flex items-center gap-2"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate" style={{ fontWeight: 400 }}>
              {title}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {[retailer, price].filter(Boolean).join(" · ") || "Suggested"}
            </p>
            {(() => {
              const info = getSizeLabelForItem(
                { category: item.category },
                activeSizeProfile ?? null,
              );
              if (info.status === "none") return null;
              const isConfirmed = info.status === "confirmed";
              return (
                <span
                  style={{
                    display: "inline-block",
                    marginTop: 4,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    padding: "2px 7px",
                    borderRadius: 999,
                    background: isConfirmed ? "var(--l2f-cream)" : "#FFF4E0",
                    color: isConfirmed ? CORAL : "#B45309",
                    border: `0.5px solid ${isConfirmed ? CORAL + "33" : "#F59E0B33"}`,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {info.label}
                </span>
              );
            })()}
          </div>

          {link && (
            <ChevronRight
              size={16}
              className="flex-shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )}
        </BuyContainer>
      </div>
    </li>
  );
}
